import React from "react";
import styles from "../components/Dashboard.module.css";
import { datasetCoverageApi } from "../services/datasetCoverageApi";
import { runsApi, DryRunResult } from "../services/runsApi";

type PairOption = {
  key: string;
  agentId: string;
  datasetId: string;
  datasetName: string | null;
  nightly: boolean;
};

const pairLabel = (pair: PairOption) =>
  `${pair.agentId} + ${pair.datasetName || pair.datasetId}${pair.nightly ? " (nightly)" : ""}`;

const parseMetadataJson = (value: string): Record<string, unknown> | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = JSON.parse(trimmed) as unknown;
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Launch metadata must be a JSON object.");
  }
  return parsed as Record<string, unknown>;
};

const RunLaunchPage: React.FC = () => {
  const [pairs, setPairs] = React.useState<PairOption[]>([]);
  const [selectedPairKey, setSelectedPairKey] = React.useState("");
  const [metaJson, setMetaJson] = React.useState("{}");
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [feedback, setFeedback] = React.useState<string | null>(null);
  const [dryRunResult, setDryRunResult] = React.useState<DryRunResult | null>(null);

  React.useEffect(() => {
    let mounted = true;
    const loadPairs = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await datasetCoverageApi.listPairs();
        if (!mounted) return;
        const rows: PairOption[] = res.data.map((p) => ({
          key: `${p.agent_id}::${p.dataset_id}`,
          agentId: p.agent_id,
          datasetId: p.dataset_id,
          datasetName: p.dataset_name,
          nightly: p.nightly,
        }));
        setPairs(rows);
        setSelectedPairKey(rows[0]?.key || "");
      } catch (e: unknown) {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : "Failed to load connected pairs.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void loadPairs();
    return () => {
      mounted = false;
    };
  }, []);

  const selectedPair = React.useMemo(
    () => pairs.find((p) => p.key === selectedPairKey) || null,
    [pairs, selectedPairKey],
  );

  const buildPayload = (): {
    agent_id: string;
    dataset_id: string;
    dataset_name?: string;
    source: "manual";
    user_payload?: Record<string, unknown>;
  } => {
    if (!selectedPair) throw new Error("Choose an agent-dataset pair first.");
    const metadata = parseMetadataJson(metaJson);
    return {
      agent_id: selectedPair.agentId,
      dataset_id: selectedPair.datasetId,
      dataset_name: selectedPair.datasetName || undefined,
      source: "manual",
      user_payload: metadata,
    };
  };

  const runDryCheck = async () => {
    setBusy(true);
    setError(null);
    setFeedback(null);
    try {
      const result = await runsApi.dryRunLaunch(buildPayload());
      setDryRunResult(result);
      setFeedback(result.ready ? "Dry-run checks passed." : "Dry-run checks found issues.");
    } catch (e: unknown) {
      setDryRunResult(null);
      setError(e instanceof Error ? e.message : "Dry-run failed.");
    } finally {
      setBusy(false);
    }
  };

  const launchRun = async () => {
    setBusy(true);
    setError(null);
    setFeedback(null);
    try {
      const run = await runsApi.launchRun(buildPayload());
      setDryRunResult(null);
      setFeedback(`Run created: ${run.id} (${run.status}).`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to launch run.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.dashboard}>
        <section className={styles.dashboardLoading} aria-live="polite" aria-busy="true">
          <div className={styles.dashboardLoadingSpinner} />
          <p className={styles.dashboardLoadingText}>Loading launch options...</p>
        </section>
      </div>
    );
  }

  return (
    <div className={styles.runsPage}>
      <section className={styles.runsTableCard}>
        <h1 className={styles.runsTitle}>Run Launch</h1>
        <p className={styles.runsSubtitle}>
          Launch manual runs from connected agent-dataset pairs or run dry checks before execution.
        </p>

        {pairs.length === 0 ? (
          <div className={styles.metricsErrorBox}>
            <p>No connected pairs found. Connect datasets to agents in the Agents tab first.</p>
          </div>
        ) : (
          <>
            <div className={styles.agentsFormGrid}>
              <label className={styles.agentsField}>
                <span>Connected pair</span>
                <select
                  className={styles.metricsInput}
                  value={selectedPairKey}
                  onChange={(e) => setSelectedPairKey(e.target.value)}
                  disabled={busy}
                >
                  {pairs.map((pair) => (
                    <option key={pair.key} value={pair.key}>
                      {pairLabel(pair)}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.agentsField}>
                <span>Mode</span>
                <input className={styles.metricsInput} value="manual" readOnly />
              </label>
            </div>

            <label className={styles.agentsField}>
              <span>Optional launch metadata (JSON object)</span>
              <textarea
                className={styles.agentsTextarea}
                spellCheck={false}
                value={metaJson}
                onChange={(e) => setMetaJson(e.target.value)}
                disabled={busy}
              />
            </label>

            <div className={styles.agentsActions}>
              <button
                type="button"
                className={styles.metricsSecondaryBtn}
                onClick={runDryCheck}
                disabled={busy || !selectedPair}
              >
                {busy ? "Working..." : "Run dry-check"}
              </button>
              <button
                type="button"
                className={styles.agentsPrimaryBtn}
                onClick={launchRun}
                disabled={busy || !selectedPair}
              >
                {busy ? "Working..." : "Launch run"}
              </button>
            </div>
          </>
        )}

        {feedback ? <div className={styles.metricsSuccessBox}>{feedback}</div> : null}
        {error ? (
          <div className={styles.metricsErrorBox}>
            <p>{error}</p>
          </div>
        ) : null}

        {dryRunResult ? (
          <div className={styles.runLaunchChecks}>
            <h3 className={styles.runLaunchChecksTitle}>
              Dry-run result: {dryRunResult.ready ? "Ready" : "Blocked"}
            </h3>
            <p className={styles.runLaunchChecksMeta}>
              Estimated duration:{" "}
              {dryRunResult.estimated_duration_ms == null
                ? "n/a"
                : `${Math.round(dryRunResult.estimated_duration_ms / 1000)}s`}
            </p>
            <ul className={styles.runLaunchChecksList}>
              {dryRunResult.checks.map((check) => (
                <li key={check.name} className={check.passed ? styles.runLaunchCheckPass : styles.runLaunchCheckFail}>
                  {check.name}: {check.passed ? "pass" : "fail"}
                  {check.message ? ` — ${check.message}` : ""}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default RunLaunchPage;
